// src/store/slices/uiSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface UIState {
	// Sidebar state
	sidebarOpen: boolean;
	sidebarCollapsed: boolean;

	// Modal states
	modals: {
		addCategory: boolean;
		editCategory: boolean;
		deleteCategory: boolean;
		addMenuItem: boolean;
		editMenuItem: boolean;
		deleteMenuItem: boolean;
		addBranch: boolean;
		editBranch: boolean;
		deleteBranch: boolean;
		organizationSetup: boolean;
	};

	// View preferences
	menuItemsViewMode: 'grid' | 'list';
	categoriesViewMode: 'grid' | 'list';

	// Loading states for specific UI components
	loadingStates: {
		savingSettings: boolean;
		uploadingImage: boolean;
		exportingData: boolean;
	};

	// Notifications
	notifications: Notification[];

	// Theme and preferences
	theme: 'light' | 'dark' | 'system';
	language: 'en' | 'ar';

	// Filters visibility
	filtersVisible: boolean;

	// Active tabs
	activeSettingsTab: string;
	activeCategoryTab: string;
}

export interface Notification {
	id: string;
	type: 'success' | 'error' | 'warning' | 'info';
	title: string;
	message: string;
	duration?: number;
	timestamp: number;
}

const initialState: UIState = {
	sidebarOpen: false,
	sidebarCollapsed: false,
	modals: {
		addCategory: false,
		editCategory: false,
		deleteCategory: false,
		addMenuItem: false,
		editMenuItem: false,
		deleteMenuItem: false,
		addBranch: false,
		editBranch: false,
		deleteBranch: false,
		organizationSetup: false,
	},
	menuItemsViewMode: 'grid',
	categoriesViewMode: 'grid',
	loadingStates: {
		savingSettings: false,
		uploadingImage: false,
		exportingData: false,
	},
	notifications: [],
	theme: 'system',
	language: 'en',
	filtersVisible: false,
	activeSettingsTab: 'brand',
	activeCategoryTab: 'main',
};

const uiSlice = createSlice({
	name: 'ui',
	initialState,
	reducers: {
		// Sidebar actions
		toggleSidebar: (state) => {
			state.sidebarOpen = !state.sidebarOpen;
		},
		setSidebarOpen: (state, action: PayloadAction<boolean>) => {
			state.sidebarOpen = action.payload;
		},
		toggleSidebarCollapsed: (state) => {
			state.sidebarCollapsed = !state.sidebarCollapsed;
		},
		setSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
			state.sidebarCollapsed = action.payload;
		},

		// Modal actions
		openModal: (state, action: PayloadAction<keyof UIState['modals']>) => {
			state.modals[action.payload] = true;
		},
		closeModal: (state, action: PayloadAction<keyof UIState['modals']>) => {
			state.modals[action.payload] = false;
		},
		closeAllModals: (state) => {
			Object.keys(state.modals).forEach(key => {
				state.modals[key as keyof UIState['modals']] = false;
			});
		},

		// View mode actions
		setMenuItemsViewMode: (state, action: PayloadAction<'grid' | 'list'>) => {
			state.menuItemsViewMode = action.payload;
		},
		setCategoriesViewMode: (state, action: PayloadAction<'grid' | 'list'>) => {
			state.categoriesViewMode = action.payload;
		},

		// Loading states
		setLoadingState: (state, action: PayloadAction<{ key: keyof UIState['loadingStates']; value: boolean }>) => {
			state.loadingStates[action.payload.key] = action.payload.value;
		},

		// Notification actions
		addNotification: (state, action: PayloadAction<Omit<Notification, 'id' | 'timestamp'>>) => {
			const notification: Notification = {
				...action.payload,
				id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
				timestamp: Date.now(),
			};
			state.notifications.push(notification);
		},
		removeNotification: (state, action: PayloadAction<string>) => {
			state.notifications = state.notifications.filter(n => n.id !== action.payload);
		},
		clearNotifications: (state) => {
			state.notifications = [];
		},

		// Theme and language
		setTheme: (state, action: PayloadAction<'light' | 'dark' | 'system'>) => {
			state.theme = action.payload;
		},
		setLanguage: (state, action: PayloadAction<'en' | 'ar'>) => {
			state.language = action.payload;
		},

		// Filters
		toggleFiltersVisibility: (state) => {
			state.filtersVisible = !state.filtersVisible;
		},
		setFiltersVisible: (state, action: PayloadAction<boolean>) => {
			state.filtersVisible = action.payload;
		},

		// Active tabs
		setActiveSettingsTab: (state, action: PayloadAction<string>) => {
			state.activeSettingsTab = action.payload;
		},
		setActiveCategoryTab: (state, action: PayloadAction<string>) => {
			state.activeCategoryTab = action.payload;
		},

		// Utility actions
		resetUIState: (state) => {
			return { ...initialState, theme: state.theme, language: state.language };
		},
	},
});

export const {
	// Sidebar
	toggleSidebar,
	setSidebarOpen,
	toggleSidebarCollapsed,
	setSidebarCollapsed,

	// Modals
	openModal,
	closeModal,
	closeAllModals,

	// View modes
	setMenuItemsViewMode,
	setCategoriesViewMode,

	// Loading states
	setLoadingState,

	// Notifications
	addNotification,
	removeNotification,
	clearNotifications,

	// Theme and language
	setTheme,
	setLanguage,

	// Filters
	toggleFiltersVisibility,
	setFiltersVisible,

	// Active tabs
	setActiveSettingsTab,
	setActiveCategoryTab,

	// Utility
	resetUIState,
} = uiSlice.actions;

export default uiSlice.reducer;